import {
  Location,
  LocationKey,
  Pathname,
  Search,
  Hash,
  LocationListener,
  Action,
} from 'history';
import {useEffect, useMemo, useRef} from 'react';
import {useHistoryOriginal} from './react-router';
import {useLocationState} from './useLocation';
import {useInitialLocation} from './context';
import {DEFAULT_SCROLL_ANCHOR_ID} from './HistoryEventHandler';

export const SCROLL_TOP_ANCHOR_ID = '@aboutyou/router::SCROLL_TOP_ANCHOR_ID';
const PUSH_UI_STATE = '@aboutyou/router::PUSH_UI_STATE';
const FORCE_ACTION_PUSH = '@aboutyou/router::FORCE_ACTION_PUSH';

export interface LocationType {
  pathname?: Pathname;
  search?: Search;
  hash?: Hash;
  key?: LocationKey;
  state?: object;
}

export interface LocationState {
  [PUSH_UI_STATE]?: true;
  [SCROLL_TOP_ANCHOR_ID]?: string;
  [FORCE_ACTION_PUSH]?: true;
}

interface ExtendedHistory extends ReturnType<typeof useHistoryOriginal> {
  pushUiUpdate: (location: LocationType | string) => void;
  pushWithScrollTopAnchor: (location: LocationType | string) => void;
  replaceUiUpdate: (location: LocationType | string) => void;
  replaceWithScrollTopAnchor: (location: LocationType | string) => void;
  replaceWithScrollToTop: (location: LocationType | string) => void;
  listen: (
    listener: LocationListener,
    config?: {disableUiUpdates: true} | {disableUiOrUrlsReplaceUpdates: true},
  ) => ReturnType<ReturnType<typeof useHistoryOriginal>['listen']>;
}

/**
 * The router allows you to listen to history changes and push them.
 * This is an extended version of the react-router. New methods are
 * * `pushUiUpdate`: Works like `push` but should be used for ui updates like opening a bottom sheet. This UI change should in some cases be ignored, e.g. for tracking
 */
export const useHistory = (): ExtendedHistory => {
  const originalHistory = useHistoryOriginal<LocationState>();
  const {protocol, host} = useInitialLocation();

  if (!originalHistory) {
    throw new TypeError(
      'A history object was not provided for `useHistory`. ' +
        'Did you forget to wrap the components with one of the `Router` types ' +
        'or a `RouterProvider` from `@aboutyou/router`?',
    );
  }

  return useMemo(() => {
    const pathToLocation = (path: string) => {
      const url = new URL(path, `${protocol}://${host}`);
      return {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
      };
    };

    const pushUiUpdate: (
      location: LocationType | string,
    ) => void = location => {
      return originalHistory.push({
        ...(typeof location === 'string' ? pathToLocation(location) : location),
        state: {
          ...(typeof location === 'string' ? {} : location.state ?? {}),
          [PUSH_UI_STATE]: true,
        },
      });
    };

    const pushWithScrollTopAnchor: (
      location: LocationType,
    ) => void = location => {
      return originalHistory.push({
        ...(typeof location === 'string' ? pathToLocation(location) : location),
        state: createRouteState({considerScrollTopAnchor: true}),
      });
    };

    const replaceUiUpdate: (
      location: LocationType | string,
    ) => void = location => {
      if (typeof location === 'object') {
        delete (location.state as LocationState)?.[PUSH_UI_STATE];
      }

      return originalHistory.replace({
        ...(typeof location === 'string' ? pathToLocation(location) : location),
        state: {
          ...(typeof location === 'string' ? {} : location.state ?? {}),
        },
      });
    };

    const replaceWithScrollTopAnchor: (
      location: LocationType,
    ) => void = location => {
      return originalHistory.replace({
        ...(typeof location === 'string' ? pathToLocation(location) : location),
        state: {
          ...(typeof location === 'string' ? {} : location.state ?? {}),
          ...createRouteState({
            considerScrollTopAnchor: true,
            forcePushBehaviour: true,
          }),
        },
      });
    };

    const replaceWithScrollToTop: (
      location: LocationType | string,
    ) => void = location => {
      return originalHistory.replace({
        ...(typeof location === 'string' ? pathToLocation(location) : location),
        state: {
          ...(typeof location === 'string' ? {} : location.state ?? {}),
          ...createRouteState({forcePushBehaviour: true}), // This will force the replace action to be treated more like a push, which in turn triggers a scroll to top
        },
      });
    };

    const listen = (
      listener: LocationListener,
      config?: {disableUiUpdates: true} | {disableUiOrUrlsReplaceUpdates: true},
    ) => {
      if (!config) {
        return originalHistory.listen(listener);
      }

      const customListener: LocationListener<LocationState> = (
        location,
        action,
      ) => {
        if (
          config &&
          'disableUiUpdates' in config &&
          isUIPush(location.state, action)
        ) {
          return;
        }
        if (
          config &&
          'disableUiOrUrlsReplaceUpdates' in config &&
          isUIOrUrlReplaceUpdate(location.state, action)
        ) {
          return;
        }
        return listener(location, action);
      };
      return originalHistory.listen(customListener);
    };

    return new Proxy(originalHistory, {
      get: function (target, prop, receiver) {
        if (prop === 'pushUiUpdate') {
          return pushUiUpdate;
        }
        if (prop === 'pushWithScrollTopAnchor') {
          return pushWithScrollTopAnchor;
        }
        if (prop === 'replaceUiUpdate') {
          return replaceUiUpdate;
        }
        if (prop === 'replaceWithScrollTopAnchor') {
          return replaceWithScrollTopAnchor;
        }
        if (prop === 'replaceWithScrollToTop') {
          return replaceWithScrollToTop;
        }
        if (prop === 'listen') {
          return listen;
        }
        return Reflect.get(target, prop, receiver);
      },
    }) as ExtendedHistory;
  }, [originalHistory, protocol, host]);
};

export const createRouteState = (locationState: {
  considerScrollTopAnchor?: boolean;
  scrollTopAnchorId?: string;
  forcePushBehaviour?: boolean;
}) => {
  const {considerScrollTopAnchor, scrollTopAnchorId, forcePushBehaviour} =
    locationState;

  if (!considerScrollTopAnchor && !forcePushBehaviour) {
    return undefined;
  }

  const state: LocationState = {};
  if (considerScrollTopAnchor) {
    state[SCROLL_TOP_ANCHOR_ID] = scrollTopAnchorId ?? DEFAULT_SCROLL_ANCHOR_ID;
  }
  if (forcePushBehaviour) {
    state[FORCE_ACTION_PUSH] = true;
  }
  return state;
};

export const getScrollTopAnchorId = (
  location: Location<LocationState | unknown>,
) => {
  return (location.state as LocationState)?.[SCROLL_TOP_ANCHOR_ID];
};

export const shouldForceActionPush = (
  location: Location<LocationState | unknown>,
) => {
  return Boolean((location.state as LocationState)?.[FORCE_ACTION_PUSH]);
};

export const useIsLastInteractionUIPushEvent = () => {
  const state = useLocationState<LocationState>();
  const history = useHistory();
  const lastAction = useRef<Action>();

  useEffect(() => {
    const removeListener = history.listen((_, action) => {
      lastAction.current = action;
    });

    return () => {
      removeListener();
    };
  }, [history]);

  return isUIPush(state, lastAction.current);
};

const isUIPush = (locationState?: LocationState, action?: Action): boolean => {
  if (action === 'PUSH') {
    return Boolean(locationState?.[PUSH_UI_STATE]);
  }

  if (action === 'POP') {
    return Boolean(
      locationState?.[PUSH_UI_STATE] && !locationState?.[FORCE_ACTION_PUSH],
    );
  }

  return false;
};

const isUIOrUrlReplaceUpdate = (
  locationState?: LocationState,
  action?: Action,
): boolean => {
  // when new location states are pushed, ex: when opening a bottomsheet
  if (action === 'PUSH' && Boolean(locationState?.[PUSH_UI_STATE])) {
    return true;
  }

  // when url changes with replace state, ex: switching colors on ADP
  if (action === 'REPLACE' && Boolean(locationState?.[FORCE_ACTION_PUSH])) {
    return true;
  }

  // when closing some overlays, ex: closing flyouts, bottomsheets
  if (action === 'POP') {
    return true;
  }

  return false;
};
